import { forwardRef, useRef, useState } from 'react';
import type { PropsWithoutRef } from 'react';

import Icon, { registerIcon } from '../Icon';
import * as caretDownIcon from '../Icon/icons/caret-down.icon';
import * as caretUpIcon from '../Icon/icons/caret-up.icon';
import * as timesIcon from '../Icon/icons/times.icon';
import type { RefElement } from '../../types';
import { useConsolidatedRef, useI18n, useTestIds, useUID } from '../../hooks';
import { StyledFormControl } from '../FormControl';
import { createClassName, withTestIds } from '../../utils';
import Flex from '../Flex';
import Button from '../Button';
import FormField from '../FormField';
import { FormDialog } from '../Dialog';

import {
  StyledClearButton,
  StyledCompositeInput,
  StyledInputButton,
  StyledInputContainer
} from './CompositeInput.styles';
import type { CompositeInputProps } from './CompositeInput.types';
import { getCompositeInputTestIds } from './CompositeInput.test-ids';

registerIcon(caretDownIcon, caretUpIcon, timesIcon);

export default withTestIds(
  forwardRef<RefElement<CompositeInputProps>, PropsWithoutRef<CompositeInputProps>>(
    function CompositeInput(props, ref) {
      const uid = useUID();
      const {
        testId,
        id = uid,
        disabled,
        value,
        onClear,
        required,
        status,
        info,
        label,
        labelHidden,
        additionalInfo,
        dialog: { renderer: Renderer, rendererProps, onApply, onCancel },
        className,
        ...restProps
      } = props;

      const testIds = useTestIds(testId, getCompositeInputTestIds);
      const t = useI18n();

      const [open, setOpen] = useState(false);

      const containerRef = useConsolidatedRef<HTMLDivElement>(ref);
      const buttonRef = useRef<HTMLInputElement>(null);

      const closePopover = () => {
        setOpen(false);
        buttonRef.current?.focus();
      };

      const labelId = `${id}-label`;

      const Comp = (
        <Flex
          as={StyledCompositeInput}
          forwardedAs={StyledFormControl}
          container={{ alignItems: 'center' }}
          status={status}
          disabled={disabled}
          ref={containerRef}
        >
          <Flex container={{ alignItems: 'center' }} item={{ grow: 1 }} as={StyledInputContainer}>
            <StyledInputButton
              data-testid={testIds.control}
              {...restProps}
              id={id}
              disabled={disabled}
              aria-haspopup='dialog'
              aria-describedby={info ? `${id}-info` : undefined}
              aria-controls={open ? `${id}-dialog` : undefined}
              className={createClassName('composite-input', className)}
              readOnly
              required={required}
              onClick={() => {
                if (!disabled) {
                  setOpen(true);
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setOpen(true);
                }
              }}
              value={value ?? ''}
              ref={buttonRef}
            />
            <Button
              icon
              variant='simple'
              onClick={() => {
                setOpen(!open);
              }}
              onMouseDown={(e: MouseEvent) => {
                e.preventDefault();
              }}
              aria-label={t(
                open ? 'composite_input_close_button_a11y' : 'composite_input_open_button_a11y'
              )}
              disabled={disabled}
              tabIndex='-1'
            >
              <Icon name={open ? 'caret-up' : 'caret-down'} />
            </Button>
            {onClear && (
              <StyledClearButton
                icon
                variant='simple'
                label={t('clear')}
                onClick={() => {
                  onClear();
                  buttonRef.current?.focus();
                }}
                disabled={disabled}
              >
                <Icon name='times' />
              </StyledClearButton>
            )}
          </Flex>

          {open && containerRef.current && (
            <FormDialog
              arrow={false}
              // FIXME: The wrath of optional labels, as a ReactNode, strikes again...
              aria-label={label as string}
              target={containerRef.current}
              placement='bottom-start'
              onCancel={() => onCancel({ close: closePopover })}
              onSubmit={{
                text: t('apply'),
                handler: () => onApply({ close: closePopover })
              }}
              id={`${id}-dialog`}
            >
              <Flex container={{ direction: 'column' }}>
                <Renderer {...rendererProps} />
              </Flex>
            </FormDialog>
          )}
        </Flex>
      );

      return label ? (
        <FormField
          testId={testIds}
          label={label}
          labelHidden={labelHidden}
          id={id}
          info={info}
          status={status}
          required={required}
          disabled={disabled}
          labelId={labelId}
          additionalInfo={additionalInfo}
        >
          {Comp}
        </FormField>
      ) : (
        Comp
      );
    }
  ) as <P extends object>(props: CompositeInputProps<P>) => JSX.Element | null,
  getCompositeInputTestIds
);
